import { useEffect, useState } from 'react';
import {
  BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { getStoolScale } from './api';
import Heatmap, {
  PERIODS, GRANULARITY, DensityLegend, CellPopup,
} from './Heatmap';

// Чувствительность: меньше — лучше, 0% = проблем не было.
function percentColor(percent) {
  if (percent <= 10) return '#4f9c41';
  if (percent <= 25) return '#d9a03c';
  return '#c2553f';
}

/** Верх оси Y: проценты здесь низкие, на шкале 0–100 столбцы были бы не видны.
 *  Округляем максимум вверх до пятёрки, но не ниже 10, чтобы 1–2% не выглядели
 *  во весь график. Точные значения всё равно подписаны над столбцами. */
function axisMax(values) {
  const peak = Math.max(0, ...values);
  return Math.max(10, Math.ceil(peak / 5) * 5);
}

// Цвет столбца зависит от значения. Через shape, а не Cell:
// Cell в recharts 3 помечен deprecated и исчезнет в 4.0.
// Передаём только геометрию: остальные служебные пропсы recharts иначе
// попали бы в атрибуты svg-элемента.
function PercentBar({ x, y, width, height, payload }) {
  return (
    <Rectangle
      x={x} y={y} width={width} height={height}
      radius={[3, 3, 0, 0]}
      fill={percentColor(payload.percent)}
    />
  );
}

/** Чувствительность кала: доля проблемных дней по календарным месяцам. */
function SensitivityChart({ sensitivityByMonth }) {
  if (!sensitivityByMonth || sensitivityByMonth.length === 0) return null;

  const yMax = axisMax(sensitivityByMonth.map(m => m.percent));

  return (
    <div className="ss-subsection">
      <div className="ss-title ss-subtitle">Чувствительность кала</div>

      <p className="ss-note">
        Доля дней в месяце с проблемами: стул отмечен и не равен баллу 2
        (нормальный). Чем ниже столбец, тем спокойнее месяц, 0% — проблем не было.
      </p>

      <ResponsiveContainer width="100%" height={170}>
        <BarChart
          data={sensitivityByMonth}
          margin={{ top: 22, right: 10, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: '#aaa' }}
            interval={0}
            stroke="#e8e0d5"
          />
          <YAxis
            domain={[0, yMax]}
            ticks={[0, yMax / 2, yMax]}
            tick={{ fontSize: 10, fill: '#888' }}
            width={38}
            tickFormatter={v => `${v}%`}
          />
          <Tooltip
            contentStyle={{ borderRadius: 8, fontSize: 13 }}
            formatter={(v, _n, item) => {
              const p = item?.payload;
              return [
                p ? `${v}% — ${p.days_problem} из ${p.days_total} дней` : `${v}%`,
                'С проблемами',
              ];
            }}
            labelFormatter={(_, payload) => {
              const p = payload?.[0]?.payload;
              return p ? `${p.date_from} — ${p.date_to}` : '';
            }}
          />
          <Bar
            dataKey="percent"
            shape={<PercentBar />}
            isAnimationActive={false}
            // 0% оставляем пустым местом, а 1–2% иначе не видны совсем
            minPointSize={v => (v > 0 ? 2 : 0)}
          >
            <LabelList
              dataKey="percent"
              position="top"
              formatter={v => `${v}%`}
              style={{ fontSize: 9, fill: '#888', fontWeight: 700 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function StoolScaleBlock({ petId }) {
  const [months, setMonths] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {level, column, count}

  useEffect(() => {
    setLoading(true);
    setActive(null);
    getStoolScale(petId, months)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [petId, months]);

  const periodTabs = (
    <div className="visits-period-tabs ss-tabs">
      {PERIODS.map(p => (
        <button
          key={p.value}
          className={`visits-tab${months === p.value ? ' visits-tab-active' : ''}`}
          onClick={() => setMonths(p.value)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  const columns = data?.columns || [];
  const levels = data?.levels || [];
  const maxCount = data?.max_count || 0;

  const handleCellClick = (row, column, count, details) => {
    setActive(prev =>
      prev && prev.row.key === row.key && prev.column.date_from === column.date_from
        ? null
        : { row, column, count, details }
    );
  };

  return (
    <div className="ss-block">
      <div className="ss-header">
        <div className="ss-title">Шкала кала</div>
        {periodTabs}
      </div>

      {loading && <p className="fr-empty">Загрузка…</p>}

      {!loading && !data && (
        <p className="fr-empty">Не удалось загрузить шкалу кала</p>
      )}

      {!loading && data && (
        <>
          <p className="ss-note">
            {GRANULARITY[months]} · чем больше отметок, тем темнее клетка.
            Идеального стула на карте нет: отметка «нормальный»
            и отсутствие отметки означают одно и то же.
          </p>

          <Heatmap
            rows={levels}
            columns={columns}
            maxCount={maxCount}
            activeCell={active}
            onCellClick={handleCellClick}
          />

          {active && (
            <CellPopup
              cell={active}
              rowTitle={`${active.row.label} · ${active.row.title}`}
              onClose={() => setActive(null)}
            />
          )}

          {data.total === 0 && (
            <p className="fr-empty ss-empty">
              За выбранный период отметок консистенции нет
            </p>
          )}

          <DensityLegend />

          <div className="ss-scale-legend">
            {levels.map(l => (
              <div key={l.key} className="ss-scale-legend-item">
                <span className="ss-scale-legend-key">{l.label}</span>
                <span className="ss-scale-legend-title">{l.title}</span>
              </div>
            ))}
          </div>

          {/* Тот же период, что и у карты — кнопки наверху общие */}
          <SensitivityChart sensitivityByMonth={data.sensitivity_by_month} />
        </>
      )}
    </div>
  );
}
